import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useResourceMutations } from "@/hooks/useResource";
import { useOptions } from "@/hooks/useOptions";
import { FormDrawer } from "@/components/kit/FormDrawer";
import { TextField, SelectField } from "@/components/kit/fields";
import { SearchSelect } from "@/components/kit/SearchSelect";

const empty = { payment_method_id: "", method: "", network: "", currency: "USDT", address: "", label: "", balance: 0, buyer_id: "" };

export function WalletDrawer({ open, item, onOpenChange, admin = false }) {
  const { user } = useAuth();
  const opts = useOptions();
  const { create, update } = useResourceMutations("wallets", {
    createMsg: admin ? "Wallet added" : "Wallet added and sent for approval",
    updateMsg: "Wallet updated",
  });
  const [form, setForm] = useState(empty);
  const baseline = useRef(JSON.stringify(empty));
  const isEdit = !!item?.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = JSON.stringify(form) !== baseline.current;

  const methods = opts.payment_methods || [];
  const userOpts = useMemo(() => {
    const seen = {};
    const all = [...(opts.buyers || []), ...(opts.responsibles || []), ...(opts.managers || [])];
    return all.filter((u) => (seen[u.id] ? false : (seen[u.id] = true))).map((u) => ({ value: u.id, label: `${u.tenant_seq != null ? `#${u.tenant_seq} ` : ""}${u.name}`, search: `#${u.tenant_seq} ${u.tenant_seq ?? ""} ${u.name} ${u.username || ""} ${u.email || ""}` }));
  }, [opts]);

  useEffect(() => {
    if (open) { const init = item ? { ...empty, ...item } : { ...empty, buyer_id: admin ? "" : user.id }; setForm(init); baseline.current = JSON.stringify(init); }
  }, [open, item, admin, user]);

  const selectedPM = useMemo(() => methods.find((m) => m.id === form.payment_method_id), [methods, form.payment_method_id]);

  const pickMethod = (id) => {
    const m = methods.find((x) => x.id === id);
    setForm((f) => ({ ...f, payment_method_id: id, method: m?.name || "", network: m?.network || "", currency: m?.currency || f.currency }));
  };

  const save = async () => {
    if (admin && !form.buyer_id) return toast.error("Select a user");
    if (!form.payment_method_id) return toast.error("Select a payment method");
    if (!form.address.trim()) return toast.error("Wallet address / details are required");
    const body = { ...form, buyer_id: form.buyer_id || user.id };
    if (isEdit) await update.mutateAsync({ id: item.id, body });
    else await create.mutateAsync(body);
    onOpenChange(false);
  };

  return (
    <FormDrawer open={open} onOpenChange={onOpenChange} title={isEdit ? "Edit Wallet" : admin ? "Add Wallet for User" : "Add Wallet"}
      subtitle={admin ? "Create a payout wallet on behalf of a user (auto-approved)" : "Save a payout method so you don't re-enter it each time"} size="md" dirty={dirty}
      onSave={save} saving={create.isPending || update.isPending} saveLabel={isEdit ? "Save changes" : "Add wallet"} testId="wallet-drawer">
      <div className="space-y-5">
        {admin && !isEdit && (
          <div>
            <label className="mb-1.5 block text-sm text-foreground">User</label>
            <SearchSelect value={form.buyer_id} onChange={(v) => set("buyer_id", v)} options={userOpts}
              placeholder="Select a user" searchPlaceholder="Search users..." widthClass="w-[360px]" testId="wallet-user" />
          </div>
        )}
        <SelectField label="Payment method" value={form.payment_method_id} onChange={pickMethod}
          placeholder={methods.length ? "Select a payment method" : "No payment methods enabled by your network"}
          options={methods.map((m) => ({ value: m.id, label: `${m.name}${m.network ? ` · ${m.network}` : ""} · ${m.currency}` }))}
          testId="wallet-method" />
        {selectedPM && (
          <div className="rounded-lg border border-white/[0.08] bg-[#0A0A0A] p-3 text-xs text-muted-foreground" data-testid="wallet-method-meta">
            Min withdrawal: <span className="font-mono text-foreground">{selectedPM.min_withdrawal || 0} {selectedPM.currency}</span>
            {" · "}Fee: <span className="font-mono text-foreground">{selectedPM.fee || 0}%</span>
          </div>
        )}
        <TextField label="Label (optional)" value={form.label} onChange={(v) => set("label", v)} placeholder="e.g. Main USDT wallet" testId="wallet-label" />
        <TextField label="Wallet address / details" value={form.address} onChange={(v) => set("address", v)}
          placeholder={selectedPM?.required_fields || "Wallet address"} testId="wallet-address" />
      </div>
    </FormDrawer>
  );
}
